/**
 * AppOnboardingPanel — Collapsed bottom strip (dumb component).
 *
 * Always visible at the bottom of the screen while onboarding is active.
 * Clicking anywhere on it (or the chevron) calls onToggle.
 */
import moment from "moment-timezone";
import { CommonImage, CommonText } from "styledComponents/common";

import ChevronIcon_SVG from "../../assets/img/icons/chevron.svg";
import GiftCreditIcon from "../../assets/img/icons/gift.webp";
import GiftCreditIconInactive from "../../assets/img/icons/gift_10_expired.webp";
import useCountdown from "../../customHooks/useCountdown";
import { AnimatePresence, motion } from "../AuthNew/shared/motionUtils";
import {
  BadgeAndInfoGroup,
  BottomPanelWrapper,
  ChevronButton,
  ChevronIcon,
  ContinueButton,
  CreditCopy,
  CreditHighlight,
  GiftIcon,
  MobileBadgeNumber,
  MobileBadgeTotal,
  MobileChevronIcon,
  MobileCollapseButton,
  MobileCreditText,
  MobileGiftIcon,
  MobileMinimizedHeader,
  MobileMinimizedPanel,
  MobileSmallBadge,
  MobileTimerChip,
  MobileTrialBanner,
  PanelInner,
  PanelLeftGroup,
  PanelRightGroup,
  PanelStepInfo,
  PanelStepSubtitle,
  PanelStepTitle,
  StepBadge,
  StepBadgeNumber,
  StepBadgeTotal,
  TabletBadgeNumber,
  TabletBadgeTotal,
  TabletBanner,
  TabletCollapseButton,
  TabletDivider,
  TabletGiftIcon,
  TabletHeaderRow,
  TabletMinimizedPanel,
  TabletSmallBadge,
  TabletTimerChip,
  TimerBox,
  TimerDivider,
  TimerValue,
  TimerValueWrapper,
} from "./shared/appOnboardingStyles";
import { panelSlideUp } from "./shared/appOnboardingStyles";

const pad = n => String(n).padStart(2, "0");

const AppOnboardingPanel = ({
  completedCount,
  currentStepIndex,
  totalSteps,
  activeStepTitle,
  activeStepDescription,
  trialConfig,
  isExpanded,
  onToggle,
  isMobile,
  isTablet,
  selectedOrganization,
}) => {
  // Convert trialStartedAt from UTC to selected timezone or fallback to moment default timezone
  // Backend returns ISO 8601 format: "2026-03-30T11:35:35.922376" (assumed UTC)
  const convertedStartTime = trialConfig?.trialStartedAt
    ? selectedOrganization?.timezone
      ? moment.utc(trialConfig.trialStartedAt).tz(selectedOrganization.timezone)
      : moment(trialConfig.trialStartedAt)
    : null;

  const durationMs = trialConfig
    ? trialConfig.durationHours * 60 * 60 * 1000
    : null;

  const { hours, minutes, seconds, isExpired } = useCountdown({
    startTime: convertedStartTime?.format() ?? null,
    durationMs,
  });

  const showTimer = trialConfig && !isExpired;

  // Mobile collapsed panel: refactored with styled components
  if (isMobile) {
    return (
      <MobileMinimizedPanel {...panelSlideUp} onClick={onToggle}>
        <MobileMinimizedHeader>
          <motion.div
            key={`mobile-badge-${completedCount}`}
            animate={{ x: [0, -4, 4, -4, 4, 0] }}
            transition={{
              duration: 0.4,
              ease: "linear",
              repeat: 2,
            }}
            style={{ display: "inline-block" }}
          >
            <MobileSmallBadge
              aria-label={`${completedCount} of ${totalSteps} steps completed`}
            >
              <svg
                width="32"
                height="32"
                viewBox="0 0 32 32"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ position: "absolute", left: 0, top: 0 }}
              >
                <circle cx="16" cy="16" r="16" fill="#EDF8FE" />
                <circle
                  cx="16"
                  cy="16"
                  r="14"
                  fill="none"
                  stroke="#2266EF"
                  strokeWidth="3"
                  strokeDasharray={2 * Math.PI * 14}
                  strokeDashoffset={
                    2 * Math.PI * 14 * (1 - completedCount / totalSteps)
                  }
                  strokeLinecap="round"
                  transform="rotate(-90 16 16)"
                  style={{ transition: "stroke-dashoffset 0.3s ease" }}
                />
              </svg>
              <MobileBadgeNumber>{completedCount}</MobileBadgeNumber>
              <MobileBadgeTotal>/{totalSteps}</MobileBadgeTotal>
            </MobileSmallBadge>{" "}
          </motion.div>
          {/* {trialConfig && showTimer && ( */}
            <MobileTimerChip isExpired={isExpired}>
              {/* {showTimer && ( */}
                <span>
                  {pad(hours)}:{pad(minutes)}:{pad(seconds)}
                </span>
              {/* )} */}
            </MobileTimerChip>
          {/* )} */}
        </MobileMinimizedHeader>

        <MobileTrialBanner style={{ margin: 0 }}>
          <MobileGiftIcon>
            <CommonImage src={isExpired ? GiftCreditIconInactive : GiftCreditIcon} alt="Gift" />
          </MobileGiftIcon>

         {!isExpired && <MobileCreditText>
            Complete these steps in 24 hours to get a{" "}
            <CreditHighlight>
              {trialConfig?.creditCurrency ?? "$"}
              {trialConfig?.creditAmount ?? 5} credit
            </CreditHighlight>{" "}
            when upgrading
          </MobileCreditText>}
          {isExpired && (
            <CommonText color="#000E2A">
              Your {trialConfig?.creditCurrency ?? "$"}
              {trialConfig?.creditAmount ?? 20} onboarding reward has expired,
              but you can still complete these steps to get started.
            </CommonText>
          )}

          <MobileCollapseButton
            $isExpanded={isExpanded}
            aria-label={
              isExpanded
                ? "Collapse onboarding panel"
                : "Expand onboarding panel"
            }
            onClick={e => {
              e.stopPropagation();
              onToggle();
            }}
          >
            <MobileChevronIcon
              as={CommonImage}
              src={ChevronIcon_SVG}
              alt="Chevron"
            />
          </MobileCollapseButton>
        </MobileTrialBanner>
      </MobileMinimizedPanel>
    );
  }

  // Tablet collapsed panel: refactored with styled components
  if (isTablet) {
    return (
      <TabletMinimizedPanel {...panelSlideUp} onClick={onToggle}>
        <TabletHeaderRow>
          <motion.div
            key={`tablet-badge-${completedCount}`}
            animate={{ x: [0, -4, 4, -4, 4, 0] }}
            transition={{
              duration: 0.4,
              ease: "linear",
              repeat: 2,
            }}
            style={{ display: "inline-block" }}
          >
            <TabletSmallBadge
              aria-label={`${completedCount} of ${totalSteps} steps completed`}
            >
              <svg
                width="60"
                height="60"
                viewBox="0 0 60 60"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ position: "absolute", left: 0, top: 2 }}
              >
                <circle cx="30" cy="30" r="30" fill="#EDF8FE" />
                <circle
                  cx="30"
                  cy="30"
                  r="27"
                  fill="none"
                  stroke="#2266EF"
                  strokeWidth="5"
                  strokeDasharray={2 * Math.PI * 27}
                  strokeDashoffset={
                    2 * Math.PI * 27 * (1 - completedCount / totalSteps)
                  }
                  strokeLinecap="round"
                  transform="rotate(-90 30 30)"
                  style={{ transition: "stroke-dashoffset 0.3s ease" }}
                />
              </svg>
              <TabletBadgeNumber style={{ display: "inline-block" }}>
                {completedCount}
              </TabletBadgeNumber>
              <TabletBadgeTotal style={{ display: "inline-block" }}>
                /{totalSteps}
              </TabletBadgeTotal>
            </TabletSmallBadge>{" "}
          </motion.div>
          <TabletBanner
          // style={{
          //   visibility: !trialConfig || isExpired ? "hidden" : "visible",
          // }}
          >
            <TabletGiftIcon>
              <CommonImage
                src={isExpired ? GiftCreditIconInactive : GiftCreditIcon}
                alt="Gift"
              />
            </TabletGiftIcon>

            {!isExpired && (
              <MobileCreditText>
                Complete these steps in 24 hours to get a {" "}
                <CreditHighlight>
                  {trialConfig?.creditCurrency ?? "$"}
                  {trialConfig?.creditAmount ?? 5} credit
                </CreditHighlight>{" "}
                when upgrading
              </MobileCreditText>
            )}
            {isExpired && (
              <CommonText color="#000E2A">
                Your {trialConfig?.creditCurrency ?? "$"}
                {trialConfig?.creditAmount ?? 20} onboarding reward has expired,
                but you can still complete these steps to get started.
              </CommonText>
            )}

            <TabletDivider />

            <TabletTimerChip isExpired={isExpired}>
              {/* {showTimer && ( */}
                <span>
                  {pad(hours)}:{pad(minutes)}:{pad(seconds)}
                </span>
              {/* )} */}
            </TabletTimerChip>
          </TabletBanner>

          <TabletCollapseButton
            $isExpanded={isExpanded}
            aria-label={
              isExpanded
                ? "Collapse onboarding panel"
                : "Expand onboarding panel"
            }
            onClick={e => {
              e.stopPropagation();
              onToggle();
            }}
          >
            <MobileChevronIcon
              as={CommonImage}
              src={ChevronIcon_SVG}
              alt="Chevron"
            />
          </TabletCollapseButton>
        </TabletHeaderRow>
      </TabletMinimizedPanel>
    );
  }

  // Desktop collapsed panel: original layout
  return (
    <BottomPanelWrapper {...panelSlideUp} onClick={onToggle}>
      <PanelInner onClick={onToggle} role="button" aria-expanded={isExpanded}>
        {/* Left group: Badge + Step Info + Continue Button */}
        <PanelLeftGroup onClick={onToggle}>
          {/* Badge and Info group */}
          <BadgeAndInfoGroup>
            {/* Step progress badge */}
            <motion.div
              key={`desktop-badge-${completedCount}`}
              animate={{ x: [0, -5, 5, -5, 5, 0] }}
              transition={{
                duration: 0.4,
                ease: "linear",
                repeat: 2,
              }}
              style={{ display: "inline-block" }}
            >
              <StepBadge
                aria-label={`${completedCount} of ${totalSteps} steps completed`}
              >
                {/* SVG Progress Ring — driven by backend completedCount only */}
                <svg
                  width="60"
                  height="60"
                  viewBox="0 0 60 60"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ position: "absolute", left: 0, top: 0 }}
                >
                  {/* Background circle */}
                  <circle cx="30" cy="30" r="30" fill="#EDF8FE" />

                  {/* Progress ring - blue arc */}
                  <circle
                    cx="30"
                    cy="30"
                    r="27"
                    fill="none"
                    stroke="#2266EF"
                    strokeWidth="5"
                    strokeDasharray={2 * Math.PI * 27}
                    strokeDashoffset={
                      2 * Math.PI * 27 * (1 - completedCount / totalSteps)
                    }
                    strokeLinecap="round"
                    transform="rotate(-90 30 30)"
                    style={{ transition: "stroke-dashoffset 0.3s ease" }}
                  />
                </svg>
                <StepBadgeNumber style={{ display: "inline-block" }}>
                  {completedCount}
                </StepBadgeNumber>
                <StepBadgeTotal style={{ display: "inline-block" }}>
                  /{totalSteps}
                </StepBadgeTotal>
              </StepBadge>{" "}
            </motion.div>
            {/* Step info */}
            <PanelStepInfo>
              <PanelStepTitle>{activeStepTitle}</PanelStepTitle>
              <PanelStepSubtitle>
                {currentStepIndex === 0
                  ? "Invite your technicians so they can receive jobs from the mobile app."
                  : "Continue setting up your account"}
              </PanelStepSubtitle>
            </PanelStepInfo>
          </BadgeAndInfoGroup>

          {/* Continue button */}
          <ContinueButton
            onClick={e => {
              e.stopPropagation();
              onToggle();
            }}
            type="button"
          >
            Continue
          </ContinueButton>
        </PanelLeftGroup>

        {/* Right group: Timer + Chevron */}
        <PanelRightGroup onClick={onToggle}>
          {/* Trial timer + credit — always render but hide when expired */}
          <TimerBox
          // style={{justifyContent: "space-between"}}
          // style={{
          //   visibility: !trialConfig || isExpired ? "hidden" : "visible",
          // }}
          >
            {/* Gift icon */}
            <GiftIcon>
              <CommonImage
                src={isExpired ? GiftCreditIconInactive : GiftCreditIcon}
                alt="Gift"
              />
            </GiftIcon>
           {!isExpired && (
             <CreditCopy>
              Complete these steps in 24 hours to get{" "}
              <CreditHighlight>
                {trialConfig?.creditCurrency ?? "$"}
                {trialConfig?.creditAmount ?? 20} credit
              </CreditHighlight>{" "}
              when upgrading
            </CreditCopy>)}
            {isExpired && (
              <CommonText style={{maxWidth: "450px" ,minWidth: "200px"}} color="#000E2A">
                Your {trialConfig?.creditCurrency ?? "$"}
                {trialConfig?.creditAmount ?? 20} onboarding reward has expired,
                but you can still complete these steps to get started.
              </CommonText>
            )}

            <TimerDivider />
            {/* {showTimer && ( */}
            <TimerValueWrapper>
              <TimerValue isExpired={isExpired}>
                {pad(hours)}:{pad(minutes)}:{pad(seconds)}
              </TimerValue>
            </TimerValueWrapper>
            {/* )} */}
          </TimerBox>

          {/* Expand / collapse chevron */}
          <ChevronButton
            $isExpanded={isExpanded}
            aria-label={
              isExpanded
                ? "Collapse onboarding panel"
                : "Expand onboarding panel"
            }
            onClick={e => {
              e.stopPropagation(); // prevent double-toggle from PanelInner click
              onToggle();
            }}
          >
            <ChevronIcon as={CommonImage} src={ChevronIcon_SVG} alt="Chevron" />
          </ChevronButton>
        </PanelRightGroup>
      </PanelInner>
    </BottomPanelWrapper>
  );
};

export default AppOnboardingPanel;
